/**
 * useReverseGeocode - Debounced reverse geocoding hook
 *
 * Calls Mapbox Geocoding API when pin position changes,
 * with 500ms debounce to handle rapid drag events.
 * Non-blocking: geocoding failure doesn't prevent form submission.
 */

import { useCallback, useRef } from 'react'
import { reverseGeocode } from '@/lib/api/mapbox-geocoding'
import { useLocationWizardStore } from '@/stores/locationWizardStore'

export function useReverseGeocode() {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const setReverseGeocodeResult = useLocationWizardStore(
    (s) => s.setReverseGeocodeResult
  )
  const setIsReverseGeocoding = useLocationWizardStore(
    (s) => s.setIsReverseGeocoding
  )

  const geocode = useCallback(
    (lat: number, lng: number) => {
      // Clear previous debounce
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      setIsReverseGeocoding(true)

      timeoutRef.current = setTimeout(async () => {
        try {
          const result = await reverseGeocode(lat, lng)
          setReverseGeocodeResult(result)
        } catch (error) {
          console.warn('Reverse geocoding failed:', error)
          // Non-blocking: user can still type address manually
        } finally {
          setIsReverseGeocoding(false)
        }
      }, 500)
    },
    [setReverseGeocodeResult, setIsReverseGeocoding]
  )

  return { geocode }
}
